import React, { useState, useEffect } from 'react';
import Button from 'ui/elements/buttons/Button';
import SectionHeading from 'ui/elements/SectionHeading';
import graphIllustration from 'ui/illustrations/graphs.svg';
import Card from 'ui/views/cards/Card';
import { CompanyConversationLinkAsUser } from 'domain/conversations/CompanyConversationAsUser';
import { companyUrls } from 'urls';
import cx from 'classnames';
import KPIGraph from 'domain/kpis/KPIGraph';
import KpiAPI from 'apis/KpiAPI';
import Resource from 'util/resource/Resource';
import { Grid, IconButton } from '@mui/material';
import { ICompany } from 'types/company';
import { CompanyViewAs } from 'domain/companies/roleUtils';
import useResource from 'util/resource/useResource';
import { KpiWithDisplay } from 'domain/kpis/types';
import KpiEditModal from 'domain/kpis/KpiEditModal';
import PopMenu from 'ui/modules/PopMenu';
import AddIcon from 'ui/elements/icons/AddIcon';
import ArchiveIcon from 'ui/elements/icons/ArchiveIcon';
import Tooltip from 'ui/elements/Tooltip';
import CustomIconButton from 'ui/elements/icons/IconButton';
import Chip from 'ui/elements/Chip';
import KpiFormDialog, { KpiFormValues } from 'pages/Company/kpis/KpiFormDialog';
import useLazyResource from 'util/resource/useLazyResource';
import useNotify from 'hooks/useNotify';
import { useCompanyLastUpdated } from 'domain/companies/profile/LastUpdated';
import { useParams, useHistory } from 'react-router';
import ShareIcon from 'ui/elements/icons/ShareIcon';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useDrag, useDrop, useDragLayer } from 'react-dnd';
import { bluePlanetTheme } from 'ui/theme';
import { quarterSpacing } from 'ui/theme/themeConstants';

interface Props {
  company: ICompany;
  className?: string;
  viewAs: CompanyViewAs;
}

interface KpiCardProps {
  kpi: KpiWithDisplay;
  viewAs: CompanyViewAs;
  onEditClick: (kpi: KpiWithDisplay) => void;
  onShareClick: (kpi: KpiWithDisplay) => void;
  className?: string;
}

const KpiCard: React.FC<KpiCardProps> = ({ kpi, viewAs, onEditClick, onShareClick, className }) => {
  const canEdit = viewAs === 'Admin';

  return (
    <Card className={cx('u-content-spacing-bottom', className)}>
      <div className="u-flex u-flex-align-center u-flex-space-between u-half-spacing-bottom">
        <div className="u-flex u-flex-align-center u-flex--gap-quarter">
          {kpi.details.archivedAt && <Chip label="Archived" fontSize="small" color="grey-medium" />}
          {kpi.display.visibility === 'Private' && (
            <Chip fontSize="small" color="grey-medium" label="Shareholders and board members" />
          )}
        </div>
        <div className="u-flex u-flex-align-center u-flex--gap-quarter">
          {canEdit && (
            <>
              <IconButton onClick={() => onEditClick(kpi)} size="small" title="Edit KPI">
                <AddIcon fontSize="small" />
              </IconButton>
              <PopMenu
                tooltip="More options"
                items={[
                  {
                    text: 'Edit KPI',
                    icon: <AddIcon />,
                    onClick: () => onEditClick(kpi),
                  },
                  {
                    text: 'Share in update',
                    icon: <ShareIcon />,
                    onClick: () => onShareClick(kpi),
                  },
                ]}
              />
            </>
          )}
        </div>
      </div>
      <KPIGraph
        name={kpi.details.name}
        unitOfMeasure={kpi.details.unitOfMeasure}
        values={kpi.values}
        displayType={kpi.display.displayType}
        trendIndicator={kpi.display.trendIndicator}
        showXAxis={kpi.display.showXAxis}
        showYAxis={kpi.display.showYAxis}
        comparisonEnabled={kpi.display.comparisonEnabled}
        comparisonPeriod={kpi.display.comparisonPeriod}
        dataPointCount={kpi.display.displayType === 'BarChart' ? kpi.display.maxDataPoints : undefined}
      />
    </Card>
  );
};

interface DraggableKpiCardProps extends KpiCardProps {
  onReorder: (draggedKpiId: string, targetKpiId: string) => void;
}

const DraggableKpiCard: React.FC<DraggableKpiCardProps> = ({
  kpi,
  viewAs,
  onEditClick,
  onShareClick,
  className,
  onReorder,
}) => {
  const [{ isDragging }, drag, preview] = useDrag({
    type: 'KPI',
    item: { kpiId: kpi.details.id, displayOrder: kpi.display.displayOrder },
    collect: monitor => ({
      isDragging: monitor.isDragging(),
    }),
  });

  // Set preview to null to prevent default preview
  useEffect(() => {
    const img = new Image();
    img.src = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';
    img.onload = () => preview(img);
  }, [preview]);

  const [{ isOver, canDrop }, drop] = useDrop({
    accept: 'KPI',
    canDrop: (item: { kpiId: string; displayOrder: number }) => item.kpiId !== kpi.details.id,
    drop: (item: { kpiId: string; displayOrder: number }) => {
      onReorder(item.kpiId, kpi.details.id);
    },
    collect: monitor => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });

  const ref = (node: HTMLDivElement | null) => {
    drag(drop(node));
  };

  return (
    <div
      ref={ref}
      style={{
        opacity: isDragging ? 0.3 : 1,
        cursor: 'grab',
        position: 'relative',
      }}
    >
      {isOver && canDrop && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            border: `2px solid ${bluePlanetTheme.bluePlanetPalette.grey.medium}`,
            backgroundColor: bluePlanetTheme.bluePlanetPalette.white,
            borderRadius: '4px',
            opacity: 1,
            pointerEvents: 'none',
            zIndex: 1,
          }}
        />
      )}
      <KpiCard kpi={kpi} viewAs={viewAs} onEditClick={onEditClick} onShareClick={onShareClick} className={className} />
    </div>
  );
};

const CardBeingDragged: React.FC<{
  kpis: KpiWithDisplay[];
  viewAs: CompanyViewAs;
  onEditClick: (kpi: KpiWithDisplay) => void;
  onShareClick: (kpi: KpiWithDisplay) => void;
  className?: string;
}> = ({ kpis, viewAs, onEditClick, onShareClick, className }) => {
  const { isDragging, item, currentOffset } = useDragLayer(monitor => ({
    item: monitor.getItem(),
    isDragging: monitor.isDragging(),
    currentOffset: monitor.getSourceClientOffset(),
  }));

  if (!isDragging || !currentOffset || !item) {
    return null;
  }

  const kpi = kpis.find(k => k.details.id === item.kpiId);
  if (!kpi) {
    return null;
  }

  return (
    <div
      style={{
        position: 'fixed',
        pointerEvents: 'none',
        zIndex: 100,
        left: 0,
        top: 0,
        width: '100%',
        height: '100%',
      }}
    >
      <div
        style={{
          transform: `translate(${currentOffset.x}px, ${currentOffset.y}px)`,
          opacity: 0.9,
          width: '400px',
        }}
      >
        <KpiCard
          kpi={kpi}
          viewAs={viewAs}
          onEditClick={onEditClick}
          onShareClick={onShareClick}
          className={className}
        />
      </div>
    </div>
  );
};

export default function Kpis({ company, viewAs, className }: Props) {
  const { resource, mutate } = useResource<KpiWithDisplay[]>(KpiAPI.kpi.list(company.id));
  const { setLastUpdated } = useCompanyLastUpdated(company.id);
  const notify = useNotify();
  const { kpiId } = useParams<{ kpiId?: string }>();
  const history = useHistory();

  const [selectedKpiForEdit, setSelectedKpiForEdit] = useState<KpiWithDisplay | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);

  const isCompanyAdmin = viewAs === 'Admin';

  // Determine which KPIs can be viewed based on viewAs role
  const canViewKpi = (kpi: KpiWithDisplay) => {
    if (viewAs === 'Admin' || viewAs === 'Shareholder' || viewAs === 'Board member') {
      return true; // Can see all KPIs
    }
    if (viewAs === 'Follower') {
      return kpi.display.visibility === 'Public'; // Can only see public KPIs
    }
    return false; // Visitors can't see KPIs
  };

  const handleShareKpi = (kpi: KpiWithDisplay) => {
    // Navigate to updates tab with KPI ID and visibility
    // UpdateForm will fetch the KPI and create the snapshot with default config
    // The post visibility will be set to match the KPI's visibility
    history.push(
      companyUrls.overview(company.slug, 'updates', {
        isPostingUpdate: true,
        embedKpi: kpi.details.id,
        kpiVisibility: kpi.display.visibility,
      }),
    );
  };

  const handleCloseModal = () => {
    setSelectedKpiForEdit(null);
    // Navigate back to kpis page without kpiId
    if (kpiId) {
      history.push(companyUrls.overview(company.slug, 'kpis'));
    }
  };

  const handleSaveSuccess = () => {
    // Refresh the KPI data
    mutate();
  };

  const [createKpi, isCreatingKpi] = useLazyResource((values: KpiFormValues) => KpiAPI.kpi.new(company.id, values), {
    onSuccess: async createdKpi => {
      setLastUpdated(new Date());
      setIsAddDialogOpen(false);
      notify('success', 'KPI added successfully');

      // Refresh the data and open edit modal for the new KPI
      const updatedResource = await mutate();

      // Find the newly created KPI by ID
      const newKpi = updatedResource?.find((kpi: KpiWithDisplay) => kpi.details.id === createdKpi.id);

      if (newKpi) {
        setSelectedKpiForEdit(newKpi);
      }
    },
    onFailure: () => notify('error', 'Failed to add KPI'),
  });

  // Handle KPI reordering
  const handleReorderKpi = async (draggedKpiId: string, targetKpiId: string) => {
    const currentKpis = resource.state === 'fetched' ? resource.resource : [];

    // Create a sorted list of KPIs to reorder (by current displayOrder, then by position in array)
    const sortedKpis = [...currentKpis].sort((a, b) => a.display.displayOrder - b.display.displayOrder);

    // Find indices in the sorted array
    const draggedIndex = sortedKpis.findIndex((kpi: KpiWithDisplay) => kpi.details.id === draggedKpiId);
    const targetIndex = sortedKpis.findIndex((kpi: KpiWithDisplay) => kpi.details.id === targetKpiId);

    if (draggedIndex === -1 || targetIndex === -1) {
      return;
    }

    if (draggedIndex === targetIndex) {
      return;
    }

    // The dragged KPI needs to move to the target index position
    const newSortOrder = targetIndex;

    // Optimistically reorder the array for immediate UI feedback
    const reorderedKpis = [...sortedKpis];
    const [removed] = reorderedKpis.splice(draggedIndex, 1);
    reorderedKpis.splice(targetIndex, 0, removed);

    // Update display orders in optimistic data
    const optimisticData = currentKpis.map((kpi: KpiWithDisplay) => {
      const newIndex = reorderedKpis.findIndex(k => k.details.id === kpi.details.id);
      if (newIndex !== -1) {
        return {
          ...kpi,
          display: {
            ...kpi.display,
            displayOrder: newIndex,
          },
        };
      }
      return kpi;
    });

    // Update UI immediately
    mutate(optimisticData, false);

    try {
      await KpiAPI.kpi.edit(company.id, draggedKpiId, { sortOrder: newSortOrder });
    } catch (error) {
      notify('error', 'Failed to reorder KPIs');
      // Revert to server data on error
      mutate();
    }
  };

  return (
    <Resource resource={resource}>
      {kpisData => {
        // Handle kpiId from URL path - open edit modal if kpiId is in URL
        useEffect(() => {
          if (kpiId) {
            const kpiToEdit = kpisData.find((kpi: KpiWithDisplay) => kpi.details.id === kpiId);
            if (kpiToEdit && !selectedKpiForEdit) {
              setSelectedKpiForEdit(kpiToEdit);
            }
          }
        }, [kpiId, kpisData]);

        // Filter KPIs based on viewAs role
        const viewableKpis = kpisData.filter(canViewKpi);
        const activeKpis = viewableKpis.filter(kpiWithDisplay => !kpiWithDisplay.details.archivedAt);
        const archivedKpis = viewableKpis.filter(kpiWithDisplay => !!kpiWithDisplay.details.archivedAt);
        const displayKpis = showArchived ? viewableKpis : activeKpis;

        // Sort KPIs by display order
        const sortedKpis = [...displayKpis].sort((a, b) => a.display.displayOrder - b.display.displayOrder);

        return activeKpis.length > 0 || showArchived ? (
          <>
            <SectionHeading heading="Key performance indicators" addMobileMargin>
              <div style={{ display: 'flex', gap: quarterSpacing, alignItems: 'center' }}>
                {isCompanyAdmin && (
                  <>
                    <Tooltip title="Add KPI">
                      <Button kind="tertiary" size="small" onClick={() => setIsAddDialogOpen(true)}>
                        Add new KPI
                      </Button>
                    </Tooltip>
                    <PopMenu
                      tooltip="KPI Options"
                      items={[
                        {
                          text: showArchived
                            ? 'Hide archived KPIs'
                            : `Show archived KPIs${archivedKpis.length > 0 ? ` (${archivedKpis.length})` : ''}`,
                          icon: <ArchiveIcon />,
                          onClick: () => setShowArchived(!showArchived),
                        },
                      ]}
                    />
                  </>
                )}
              </div>
            </SectionHeading>

            {isCompanyAdmin ? (
              <DndProvider backend={HTML5Backend}>
                <CardBeingDragged
                  kpis={sortedKpis}
                  viewAs={viewAs}
                  onEditClick={setSelectedKpiForEdit}
                  onShareClick={handleShareKpi}
                  className={className}
                />
                <Grid container spacing={2} alignItems="stretch">
                  {sortedKpis.map(kpiWithDisplay => (
                    <Grid key={`${kpiWithDisplay.details.id}-${kpiWithDisplay.display.id}`} item xs={12} sm={6} xl={4}>
                      <DraggableKpiCard
                        kpi={kpiWithDisplay}
                        viewAs={viewAs}
                        onEditClick={setSelectedKpiForEdit}
                        onShareClick={handleShareKpi}
                        onReorder={handleReorderKpi}
                        className={className}
                      />
                    </Grid>
                  ))}
                </Grid>
              </DndProvider>
            ) : (
              <Grid container spacing={2} alignItems="stretch">
                {sortedKpis.map(kpiWithDisplay => (
                  <Grid key={`${kpiWithDisplay.details.id}-${kpiWithDisplay.display.id}`} item xs={12} sm={6} xl={4}>
                    <KpiCard
                      kpi={kpiWithDisplay}
                      viewAs={viewAs}
                      onEditClick={setSelectedKpiForEdit}
                      onShareClick={handleShareKpi}
                      className={className}
                    />
                  </Grid>
                ))}
              </Grid>
            )}

            {selectedKpiForEdit && (
              <KpiEditModal
                kpi={selectedKpiForEdit}
                companyId={company.id}
                isOpen={!!selectedKpiForEdit}
                onClose={handleCloseModal}
                onSuccess={handleSaveSuccess}
              />
            )}

            {isAddDialogOpen && (
              <KpiFormDialog
                type="Add"
                companyId={company.id}
                izSubmitting={isCreatingKpi}
                onSubmit={createKpi}
                onClose={() => setIsAddDialogOpen(false)}
              />
            )}
          </>
        ) : (
          <>
            <SectionHeading heading="Key performance indicators" addMobileMargin>
              {isCompanyAdmin && (
                <Tooltip title="Add KPI">
                  <span>
                    <CustomIconButton color="indigo" hoverColor="blue" onClick={() => setIsAddDialogOpen(true)}>
                      <AddIcon fontSize="small" />
                    </CustomIconButton>
                  </span>
                </Tooltip>
              )}
            </SectionHeading>
            <Card
              type={isCompanyAdmin ? 'dashed' : 'default'}
              data-intercom-target="kpi-widget"
              className="u-content-spacing-bottom u-relative"
            >
              <div className="u-flex u-flex--column u-flex-align-center">
                {!isCompanyAdmin ? (
                  <>
                    <img style={{ maxWidth: '10rem' }} src={graphIllustration} />
                    <p className="text-body u-content-spacing-y">
                      Reach out to the company and suggest which KPIs you would like to see here. Letting them know will
                      help both of you get the most out of CrowdWorks!
                    </p>
                    <CompanyConversationLinkAsUser
                      company={company}
                      trackingKey="company-suggest-kpi"
                      href={companyUrls.inbox.showAsUser(company.slug)}
                    >
                      <Button kind="tertiary">Suggest KPIs</Button>
                    </CompanyConversationLinkAsUser>
                  </>
                ) : (
                  <>
                    <img style={{ maxWidth: '10rem' }} src={graphIllustration} />
                    <p className="text-body u-content-spacing-y">
                      Tracking KPIs gives your company and investors a clear sense of direction, helping you achieve
                      your company&apos;s goals.
                    </p>
                    <Button kind="tertiary" onClick={() => setIsAddDialogOpen(true)}>
                      Add your first KPI
                    </Button>
                  </>
                )}
              </div>
            </Card>

            {isAddDialogOpen && (
              <KpiFormDialog
                type="Add"
                companyId={company.id}
                izSubmitting={isCreatingKpi}
                onSubmit={createKpi}
                onClose={() => setIsAddDialogOpen(false)}
              />
            )}
          </>
        );
      }}
    </Resource>
  );
}
